import { useEffect, useState } from "react";
import { Copy, Eye, Mail, MousePointerClick, Phone, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "@/lib/api";
import { ReportEmailDialog } from "@/components/admin/ReportEmailDialog";

const ctr = (c, v) => (v ? `${Math.min(100, (c / v) * 100).toFixed(1)}%` : "—");

const status = (b) => {
  if (!b.featured) return { label: "Not featured", cls: "bg-ph-raised text-ph-muted" };
  if (!b.active) return { label: `Ended ${formatDate(b.featured_until)}`, cls: "bg-ph-raised text-ph-ink/85" };
  return { label: b.featured_until ? `Live until ${formatDate(b.featured_until)}` : "Live · no end date", cls: "bg-emerald-50 text-emerald-800" };
};

const report = (b) => [
  `PRICEHUB Featured Spotlight report — ${b.name}`,
  `Status: ${status(b).label}`,
  `Spotlight views: ${b.views} (last 30 days: ${b.views_30d})`,
  `Spotlight clicks: ${b.clicks} (last 30 days: ${b.clicks_30d})`,
  `Click-through rate: ${ctr(b.clicks, b.views)}`,
  `Price enquiries: ${b.enquiries ?? 0} (last 30 days: ${b.enquiries_30d ?? 0})`,
].join("\n");

const NO_ROWS = [];

export function FeaturedStats({ rows: rawRows }) {
  const initialRows = Array.isArray(rawRows) ? rawRows : NO_ROWS;
  const [rows, setRows] = useState(initialRows);
  const [mailRow, setMailRow] = useState(null);
  useEffect(() => { setRows(initialRows); }, [initialRows]);
  const onSent = (id, email, at) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, contact_email: email, last_report_at: at } : r)));
  const copy = async (b) => {
    try { await navigator.clipboard.writeText(report(b)); toast.success(`Report for ${b.name} copied`, { description: "Paste it into an email or WhatsApp message." }); }
    catch { toast.error("Could not copy — your browser blocked clipboard access"); }
  };
  return (
    <div className="rounded-3xl border border-ph-line bg-ph-surface p-6" data-testid="panel-featured-stats">
      <div className="flex items-center justify-between">
        <p className="flex items-center gap-2 font-display text-lg font-bold text-ph-ink"><Sparkles className="h-4 w-4 text-ph-accent" /> Featured spotlight performance</p>
        <p className="hidden text-xs text-ph-muted sm:block">Views & clicks are counted once per visitor every 30 minutes</p>
      </div>
      {rows.length === 0 ? (
        <p className="py-6 text-center text-sm text-ph-muted">No featured brands yet. Tick “Featured” on a brand to start tracking.</p>
      ) : (
        <div className="mt-4 overflow-x-auto" data-lenis-prevent>
          <table className="w-full min-w-[720px] text-sm">
            <thead>
              <tr className="text-left font-mono-ph text-[11px] uppercase tracking-wider text-ph-muted">
                <th className="pb-3 font-medium">Brand</th><th className="pb-3 font-medium">Status</th>
                <th className="pb-3 text-right font-medium"><Eye className="mr-1 inline h-3.5 w-3.5" />Views</th>
                <th className="pb-3 text-right font-medium"><MousePointerClick className="mr-1 inline h-3.5 w-3.5" />Clicks</th>
                <th className="pb-3 text-right font-medium">CTR</th>
                <th className="pb-3 text-right font-medium"><Phone className="mr-1 inline h-3.5 w-3.5" />Enquiries</th><th className="pb-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-ph-line">
              {rows.map((b) => {
                const s = status(b);
                return (
                  <tr key={b.id} data-testid={`featured-stats-row-${b.slug}`}>
                    <td className="py-3 font-display font-bold text-ph-ink">{b.name}</td>
                    <td className="py-3"><span className={`rounded-full px-2.5 py-1 text-xs font-medium ${s.cls}`}>{s.label}</span></td>
                    <td className="py-3 text-right font-mono-ph">{b.views}<span className="block text-[11px] text-ph-muted">{b.views_30d} in 30d</span></td>
                    <td className="py-3 text-right font-mono-ph" data-testid={`featured-stats-clicks-${b.slug}`}>{b.clicks}<span className="block text-[11px] text-ph-muted">{b.clicks_30d} in 30d</span></td>
                    <td className="py-3 text-right font-mono-ph font-semibold text-ph-accent">{ctr(b.clicks, b.views)}</td>
                    <td className="py-3 text-right font-mono-ph" data-testid={`featured-stats-enquiries-${b.slug}`}>{b.enquiries ?? 0}<span className="block text-[11px] text-ph-muted">{b.enquiries_30d ?? 0} in 30d</span></td>
                    <td className="py-3 text-right">
                      <div className="flex justify-end gap-1.5">
                        <button type="button" onClick={() => setMailRow(b)} data-testid={`featured-stats-email-${b.slug}`}
                          className="inline-flex items-center gap-1.5 rounded-full bg-ph-lime px-3 py-1.5 text-xs font-semibold text-ph-navy hover:shadow-[0_0_24px_rgba(182,255,92,0.45)]">
                          <Mail className="h-3.5 w-3.5" /> Email report
                        </button>
                        <button type="button" onClick={() => copy(b)} data-testid={`featured-stats-copy-${b.slug}`} aria-label="Copy report"
                          className="inline-flex items-center rounded-full border border-ph-line px-2.5 py-1.5 text-xs font-semibold text-ph-ink hover:bg-ph-raised">
                          <Copy className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      {b.last_report_at && <p className="mt-1 text-[11px] text-ph-muted" data-testid={`featured-stats-last-sent-${b.slug}`}>Sent {formatDate(b.last_report_at)}</p>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <ReportEmailDialog row={mailRow} open={!!mailRow} onOpenChange={(v) => { if (!v) setMailRow(null); }} onSent={onSent} />
    </div>
  );
}
